import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Building2,
  CheckCircle2,
  ExternalLink,
  MessageCircle,
  RefreshCw,
  Send,
  Sparkles,
  Workflow,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { usePage } from "../../hooks/usePage";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { Spinner } from "../../components/ui/Spinner";
import { Modal } from "../../components/ui/Modal";
import { useToast } from "../../hooks/useToast";
import {
  connectSupportSocket,
  supportApi,
  SupportConversation,
  SupportConversationStatus,
  SupportMessage,
  SupportSocketEvent,
  SupportTypingEvent,
  SupportWorkflowPreset,
  SupportWorkflowStatus,
  SupportWorkflowType,
} from "../../api/support";

const statusColors: Record<
  SupportConversationStatus,
  "success" | "danger" | "warning" | "info" | "neutral"
> = {
  open: "success",
  pending: "warning",
  resolved: "info",
  closed: "neutral",
};

const STATUS_OPTIONS: Array<SupportConversationStatus | ""> = [
  "",
  "open",
  "pending",
  "resolved",
  "closed",
];

const WORKFLOW_STATUS_OPTIONS: SupportWorkflowStatus[] = [
  "active",
  "waiting_on_user",
  "escalated",
  "resolved",
];

const formatDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString("en-NG", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "No activity";

function mergeConversation(
  items: SupportConversation[],
  next: SupportConversation,
) {
  const filtered = items.filter((item) => item.id !== next.id);
  return [next, ...filtered].sort((a, b) => {
    const aTime = new Date(a.lastMessageAt ?? a.createdAt).getTime();
    const bTime = new Date(b.lastMessageAt ?? b.createdAt).getTime();
    return bTime - aTime;
  });
}

function appendMessage(items: SupportMessage[], next: SupportMessage) {
  if (items.some((item) => item.id === next.id)) return items;
  return [...items, next].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
}

/**
 * Who is asking, whichever side of the product they came from.
 *
 * A business thread names the *business*, not the member: an agent opening a
 * queue needs to know whose money and whose books are involved, and the
 * individual who happened to type the message is the second question. The
 * member is carried alongside so it can still be shown, and is null once
 * they have left the company.
 */
function askerOf(conversation: SupportConversation): {
  name: string;
  detail: string | null;
  isBusiness: boolean;
} {
  if (conversation.business) {
    return {
      name: conversation.business.name,
      detail: conversation.business.member?.name ?? "Former member",
      isBusiness: true,
    };
  }
  return {
    name: conversation.user?.name || "PayIQ user",
    detail: null,
    isBusiness: false,
  };
}

export const SupportChat: React.FC = () => {
  usePage("Support Chat");
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState<SupportConversation[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<SupportConversation | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [status, setStatus] = useState<SupportConversationStatus | "">("open");
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [workflows, setWorkflows] = useState<SupportWorkflowPreset[]>([]);
  const [workflowUpdating, setWorkflowUpdating] = useState(false);
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const [remoteTyping, setRemoteTyping] = useState<SupportTypingEvent | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const typingStopRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deepLinkedConversationId = searchParams.get("conversationId");

  const fetchList = useCallback(async () => {
    try {
      setLoadingList(true);
      const data = await supportApi.list({
        page: 1,
        limit: 50,
        status,
        assignedToMe,
      });
      setConversations(data.items);
      if (!selectedId && data.items[0]) setSelectedId(data.items[0].id);
    } catch (error) {
      toast.error(error, "Could not load support conversations.");
    } finally {
      setLoadingList(false);
    }
  }, [assignedToMe, selectedId, status, toast]);

  const fetchThread = useCallback(
    async (id: string) => {
      try {
        setLoadingThread(true);
        const thread = await supportApi.get(id);
        setSelected(thread.conversation);
        setMessages(thread.messages);
        setConversations((items) =>
          mergeConversation(items, thread.conversation),
        );
      } catch (error) {
        toast.error(error, "Could not open support conversation.");
      } finally {
        setLoadingThread(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  useEffect(() => {
    supportApi
      .workflows()
      .then(setWorkflows)
      .catch((error) => toast.error(error, "Could not load support workflows."));
  }, [toast]);

  useEffect(() => {
    if (!deepLinkedConversationId) return;
    setStatus("");
    setAssignedToMe(false);
    setSelectedId(deepLinkedConversationId);
  }, [deepLinkedConversationId]);

  useEffect(() => {
    if (selectedId) void fetchThread(selectedId);
  }, [fetchThread, selectedId]);

  useEffect(() => {
    const ws = connectSupportSocket((event: SupportSocketEvent) => {
      if (event.type === "conversation.updated") {
        setConversations((items) =>
          mergeConversation(items, event.conversation),
        );
        setSelected((current) =>
          current?.id === event.conversation.id ? event.conversation : current,
        );
      }
      if (event.type === "message.created" && event.conversationId === selectedId) {
        setMessages((items) => appendMessage(items, event.message));
        setRemoteTyping(null);
      }
      if (event.type === "typing.changed") {
        setRemoteTyping((current) => {
          if (event.conversationId !== selectedId || event.senderType !== "user") {
            return current;
          }
          return event.isTyping ? event : null;
        });
      }
    });
    wsRef.current = ws;
    return () => {
      ws?.close();
      wsRef.current = null;
    };
  }, [selectedId]);

  useEffect(() => {
    if (!remoteTyping?.isTyping) return;
    const timer = setTimeout(() => setRemoteTyping(null), 3500);
    return () => clearTimeout(timer);
  }, [remoteTyping?.at, remoteTyping?.isTyping]);

  const sendTyping = useCallback((isTyping: boolean) => {
    if (!selectedId || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }
    wsRef.current.send(JSON.stringify({
      type: "typing.changed",
      conversationId: selectedId,
      isTyping,
    }));
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) return;

    if (typingStopRef.current) {
      clearTimeout(typingStopRef.current);
      typingStopRef.current = null;
    }

    if (!draft.trim()) {
      sendTyping(false);
      return;
    }

    sendTyping(true);
    typingStopRef.current = setTimeout(() => {
      sendTyping(false);
      typingStopRef.current = null;
    }, 1600);

    return () => {
      if (typingStopRef.current) {
        clearTimeout(typingStopRef.current);
        typingStopRef.current = null;
      }
    };
  }, [draft, selectedId, sendTyping]);

  const selectedStatus = selected?.status ?? "open";
  const canSend = draft.trim().length > 0 && Boolean(selected) && !sending;
  const showRemoteTyping =
    Boolean(selected) &&
    Boolean(remoteTyping) &&
    remoteTyping?.conversationId === selected?.id &&
    remoteTyping?.senderType === "user" &&
    remoteTyping?.isTyping === true;

  const conversationTitle = useMemo(() => {
    if (!selected) return "Select a conversation";
    return selected.subject || selected.category || "Support conversation";
  }, [selected]);

  async function sendMessage() {
    if (!selected || !canSend) return;
    const body = draft.trim();
    try {
      setSending(true);
      sendTyping(false);
      const message = await supportApi.sendMessage(selected.id, body);
      setMessages((items) => appendMessage(items, message));
      setDraft("");
    } catch (error) {
      toast.error(error, "Could not send message.");
    } finally {
      setSending(false);
    }
  }

  async function assignToMe() {
    if (!selected) return;
    try {
      const thread = await supportApi.assignToMe(selected.id);
      setSelected(thread.conversation);
      setMessages(thread.messages);
      setConversations((items) => mergeConversation(items, thread.conversation));
      toast.success("Conversation assigned to you.");
    } catch (error) {
      toast.error(error, "Could not assign conversation.");
    }
  }

  async function updateStatus(nextStatus: SupportConversationStatus) {
    if (!selected) return;
    try {
      const thread = await supportApi.update(selected.id, { status: nextStatus });
      setSelected(thread.conversation);
      setMessages(thread.messages);
      setConversations((items) => mergeConversation(items, thread.conversation));
      toast.success("Conversation updated.");
    } catch (error) {
      toast.error(error, "Could not update conversation.");
    }
  }

  async function updateWorkflow(payload: {
    workflowType?: SupportWorkflowType;
    workflowStatus?: SupportWorkflowStatus;
    checklistItemId?: string;
    checked?: boolean;
    linkedEntityType?: string;
    linkedEntityId?: string;
    note?: string;
  }) {
    if (!selected) return;
    try {
      setWorkflowUpdating(true);
      const thread = await supportApi.updateWorkflow(selected.id, payload);
      setSelected(thread.conversation);
      setMessages(thread.messages);
      setConversations((items) => mergeConversation(items, thread.conversation));
      toast.success("Workflow updated.");
    } catch (error) {
      toast.error(error, "Could not update workflow.");
    } finally {
      setWorkflowUpdating(false);
    }
  }

  const selectStyle: React.CSSProperties = {
    height: 38,
    padding: "0 32px 0 12px",
    fontSize: 13,
    color: "var(--neutral-700)",
    background: "var(--neutral-100)",
    border: "1px solid var(--neutral-300)",
    borderRadius: "var(--radius-md)",
    outline: "none",
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 360px) 1fr", gap: 20, minHeight: "calc(100vh - 150px)" }}>
      <Card padding={0}>
        <div style={{ padding: 16, borderBottom: "1px solid var(--neutral-300)", display: "flex", gap: 10, alignItems: "center" }}>
          <select value={status} onChange={(e) => setStatus(e.target.value as SupportConversationStatus | "")} style={selectStyle}>
            {STATUS_OPTIONS.map((value) => (
              <option key={value || "all"} value={value}>
                {value ? value.charAt(0).toUpperCase() + value.slice(1) : "All statuses"}
              </option>
            ))}
          </select>
          <Button size="sm" variant={assignedToMe ? "primary" : "secondary"} onClick={() => setAssignedToMe((value) => !value)}>
            Mine
          </Button>
          <Button size="sm" variant="secondary" onClick={() => void fetchList()}>
            <RefreshCw size={14} />
          </Button>
        </div>

        <div style={{ maxHeight: "calc(100vh - 230px)", overflowY: "auto" }}>
          {loadingList ? (
            <div style={{ padding: 40, display: "flex", justifyContent: "center" }}>
              <Spinner size={24} />
            </div>
          ) : conversations.length === 0 ? (
            <div style={{ padding: 32, color: "var(--neutral-500)", textAlign: "center", fontSize: 13 }}>
              No support conversations yet
            </div>
          ) : (
            conversations.map((conversation) => (
              <button
                key={conversation.id}
                onClick={() => {
                  setSelectedId(conversation.id);
                  setSearchParams({ conversationId: conversation.id });
                }}
                style={{
                  width: "100%",
                  border: 0,
                  borderBottom: "1px solid var(--neutral-200)",
                  background:
                    selectedId === conversation.id
                      ? "var(--blue-100)"
                      : "transparent",
                  padding: 16,
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 6 }}>
                  <strong style={{ fontSize: 14, color: "var(--neutral-800)", display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                    {askerOf(conversation).isBusiness ? (
                      <Building2 size={13} color="var(--neutral-500)" style={{ flexShrink: 0 }} />
                    ) : null}
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {askerOf(conversation).name}
                    </span>
                  </strong>
                  <Badge variant={statusColors[conversation.status]}>
                    {conversation.status}
                  </Badge>
                </div>
                <div style={{ fontSize: 13, color: "var(--neutral-700)", marginBottom: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {conversation.lastMessagePreview || conversation.subject || "No message yet"}
                </div>
                <div style={{ fontSize: 11, color: "var(--neutral-500)" }}>
                  {askerOf(conversation).detail
                    ? `${askerOf(conversation).detail} · `
                    : ""}
                  {conversation.assignedAdminName || "Unassigned"} · {formatDate(conversation.lastMessageAt || conversation.createdAt)}
                </div>
                {conversation.triageCategory ? (
                  <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>
                    <Sparkles size={12} color="var(--blue-600)" />
                    <span style={{ fontSize: 11, color: "var(--blue-700)", textTransform: "capitalize" }}>
                      {conversation.triageCategory.replace(/_/g, " ")}
                    </span>
                  </div>
                ) : null}
              </button>
            ))
          )}
        </div>
      </Card>

      <Card padding={0}>
        {!selected ? (
          <div style={{ height: "100%", minHeight: 420, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--neutral-500)", gap: 10 }}>
            <MessageCircle size={22} />
            Select a conversation
          </div>
        ) : (
          <div style={{ minHeight: "calc(100vh - 150px)", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: 18, borderBottom: "1px solid var(--neutral-300)", display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center" }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, color: "var(--neutral-800)" }}>{conversationTitle}</h2>
                <div style={{ marginTop: 4, fontSize: 12, color: "var(--neutral-500)" }}>
                  {askerOf(selected).name}
                  {askerOf(selected).detail ? ` · ${askerOf(selected).detail}` : ""}
                  {" · "}
                  {selected.assignedAdminName || "Unassigned"}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Badge variant={statusColors[selectedStatus]}>{selectedStatus}</Badge>
                <Button size="sm" variant="secondary" onClick={() => void assignToMe()}>
                  Assign to me
                </Button>
                <select value={selectedStatus} onChange={(e) => void updateStatus(e.target.value as SupportConversationStatus)} style={selectStyle}>
                  {STATUS_OPTIONS.filter(Boolean).map((value) => (
                    <option key={value} value={value}>
                      {value.charAt(0).toUpperCase() + value.slice(1)}
                    </option>
                  ))}
                </select>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setWorkflowModalOpen(true)}
                  title="Open workflow"
                >
                  <Workflow size={15} />
                </Button>
              </div>
            </div>

            <div style={{ flex: 1, padding: 20, overflowY: "auto", background: "var(--neutral-100)" }}>
              {loadingThread ? (
                <div style={{ padding: 40, display: "flex", justifyContent: "center" }}>
                  <Spinner size={24} />
                </div>
              ) : (
                <>
                  {messages.map((message) => (
                    <MessageRow key={message.id} message={message} />
                  ))}
                  {showRemoteTyping ? (
                    <TypingIndicator
                      label={`${
                        selected ? askerOf(selected).detail || askerOf(selected).name : "User"
                      } is typing`}
                    />
                  ) : null}
                </>
              )}
            </div>

            <div style={{ padding: 16, borderTop: "1px solid var(--neutral-300)", display: "flex", gap: 12 }}>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Type a reply..."
                rows={3}
                style={{
                  flex: 1,
                  resize: "none",
                  border: "1px solid var(--neutral-300)",
                  borderRadius: "var(--radius-md)",
                  padding: 12,
                  fontFamily: "var(--font-family)",
                  fontSize: 14,
                  outline: "none",
                }}
              />
              <Button onClick={() => void sendMessage()} disabled={!canSend}>
                <Send size={16} />
                Send
              </Button>
            </div>
          </div>
        )}
      </Card>

      {selected ? (
        <Modal
          isOpen={workflowModalOpen}
          onClose={() => setWorkflowModalOpen(false)}
          title="PayIQ workflow"
          maxWidth={760}
        >
          <SupportWorkflowPanel
            conversation={selected}
            workflows={workflows}
            updating={workflowUpdating}
            onUpdate={(payload) => void updateWorkflow(payload)}
          />
        </Modal>
      ) : null}
    </div>
  );
};

function SupportWorkflowPanel({
  conversation,
  workflows,
  updating,
  onUpdate,
}: {
  conversation: SupportConversation;
  workflows: SupportWorkflowPreset[];
  updating: boolean;
  onUpdate: (payload: {
    workflowType?: SupportWorkflowType;
    workflowStatus?: SupportWorkflowStatus;
    checklistItemId?: string;
    checked?: boolean;
    linkedEntityType?: string;
    linkedEntityId?: string;
    note?: string;
  }) => void;
}) {
  const [note, setNote] = useState("");
  const [linkedEntityType, setLinkedEntityType] = useState("transaction");
  const [linkedEntityId, setLinkedEntityId] = useState("");
  const activePreset =
    workflows.find((workflow) => workflow.type === conversation.workflowType) ??
    workflows.find((workflow) => workflow.type === conversation.suggestedWorkflowType) ??
    null;
  const started = conversation.workflowStatus !== "not_started";
  const checklist = conversation.workflowChecklist ?? [];
  const completed = checklist.filter((item) => item.checked).length;

  function submitNote() {
    const trimmed = note.trim();
    if (!trimmed) return;
    onUpdate({ note: trimmed });
    setNote("");
  }

  function linkEntity() {
    const trimmed = linkedEntityId.trim();
    if (!trimmed) return;
    onUpdate({ linkedEntityType, linkedEntityId: trimmed });
    setLinkedEntityId("");
  }

  return (
    <div
      style={{
        background: "white",
        border: "1px solid var(--neutral-300)",
        borderRadius: "var(--radius-lg)",
        padding: 14,
        marginBottom: 18,
        boxShadow: "var(--shadow-sm)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
        <div style={{ display: "flex", gap: 10 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: "var(--radius-md)",
              background: "var(--neutral-100)",
              color: "var(--neutral-700)",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <Workflow size={17} />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--neutral-800)" }}>
              PayIQ workflow
            </div>
            <div style={{ marginTop: 5, display: "flex", flexWrap: "wrap", gap: 8 }}>
              <Badge variant={started ? "info" : "neutral"}>
                {activePreset?.label || "Not selected"}
              </Badge>
              <Badge variant={conversation.workflowStatus === "resolved" ? "success" : "warning"}>
                {conversation.workflowStatus.replace(/_/g, " ")}
              </Badge>
              {checklist.length ? (
                <span style={{ fontSize: 12, color: "var(--neutral-500)", alignSelf: "center" }}>
                  {completed}/{checklist.length} checks
                </span>
              ) : null}
            </div>
          </div>
        </div>
        {conversation.workflowType ? (
          <select
            value={conversation.workflowStatus}
            disabled={updating}
            onChange={(event) =>
              onUpdate({ workflowStatus: event.target.value as SupportWorkflowStatus })
            }
            style={{
              height: 32,
              border: "1px solid var(--neutral-300)",
              borderRadius: "var(--radius-md)",
              padding: "0 10px",
              fontSize: 12,
              color: "var(--neutral-700)",
              background: "var(--neutral-100)",
            }}
          >
            {WORKFLOW_STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {conversation.workflowType ? (
        <>
          {activePreset?.description ? (
            <p style={{ margin: "12px 0 0", fontSize: 13, lineHeight: 1.5, color: "var(--neutral-600)" }}>
              {activePreset.description}
            </p>
          ) : null}
          <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
            {checklist.map((item) => (
              <label
                key={item.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 13,
                  color: "var(--neutral-700)",
                }}
              >
                <input
                  type="checkbox"
                  checked={item.checked}
                  disabled={updating}
                  onChange={(event) =>
                    onUpdate({
                      checklistItemId: item.id,
                      checked: event.target.checked,
                    })
                  }
                />
                <span>{item.label}</span>
              </label>
            ))}
          </div>
        </>
      ) : (
        <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {workflows.map((workflow) => (
            <Button
              key={workflow.type}
              size="sm"
              variant={workflow.type === conversation.suggestedWorkflowType ? "primary" : "secondary"}
              disabled={updating}
              onClick={() => onUpdate({ workflowType: workflow.type })}
            >
              {workflow.type === conversation.suggestedWorkflowType ? <Sparkles size={13} /> : null}
              {workflow.label}
            </Button>
          ))}
        </div>
      )}

      <div
        style={{
          marginTop: 14,
          paddingTop: 14,
          borderTop: "1px solid var(--neutral-200)",
          display: "grid",
          gap: 10,
        }}
      >
        <WorkflowContext conversation={conversation} />

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select
            value={linkedEntityType}
            onChange={(event) => setLinkedEntityType(event.target.value)}
            style={{
              height: 34,
              border: "1px solid var(--neutral-300)",
              borderRadius: "var(--radius-md)",
              padding: "0 10px",
              fontSize: 12,
              background: "var(--neutral-100)",
            }}
          >
            <option value="transaction">Transaction</option>
            <option value="order">Order</option>
            <option value="card">Card</option>
            <option value="user">User</option>
          </select>
          <input
            value={linkedEntityId}
            onChange={(event) => setLinkedEntityId(event.target.value)}
            placeholder="Reference or ID"
            style={{
              flex: "1 1 180px",
              minWidth: 0,
              height: 34,
              border: "1px solid var(--neutral-300)",
              borderRadius: "var(--radius-md)",
              padding: "0 10px",
              fontSize: 12,
            }}
          />
          <Button size="sm" variant="secondary" disabled={updating || !linkedEntityId.trim()} onClick={linkEntity}>
            Link
          </Button>
        </div>

        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Add internal workflow note..."
          rows={2}
          style={{
            resize: "none",
            border: "1px solid var(--neutral-300)",
            borderRadius: "var(--radius-md)",
            padding: 10,
            fontFamily: "var(--font-family)",
            fontSize: 13,
            outline: "none",
          }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
          <div style={{ fontSize: 12, color: "var(--neutral-500)" }}>
            {conversation.workflowHistory.length
              ? `${conversation.workflowHistory.length} workflow events recorded`
              : "No workflow events yet"}
          </div>
          <Button size="sm" disabled={updating || !note.trim()} onClick={submitNote}>
            <CheckCircle2 size={14} />
            Add note
          </Button>
        </div>
      </div>
    </div>
  );
}

function SupportImageLightbox({ imageUrl, onClose }: { imageUrl: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const closeButtonStyle: React.CSSProperties = {
    position: "absolute",
    top: 24,
    right: 24,
    width: 44,
    height: 44,
    borderRadius: "var(--radius-full)",
    background: "rgba(0,0,0,0.55)",
    color: "#fff",
    fontSize: 22,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.9)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <button type="button" onClick={onClose} style={closeButtonStyle}>
        ×
      </button>
      <img
        src={imageUrl}
        alt="Support attachment preview"
        onClick={(event) => event.stopPropagation()}
        style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain" }}
      />
    </div>
  );
}

function WorkflowContext({
  conversation,
}: {
  conversation: SupportConversation;
}) {
  const context = conversation.workflowContext;
  if (!context) return null;

  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
      <span style={{ fontSize: 12, color: "var(--neutral-600)" }}>
        Tier {context.userTier.replace("tier_", "")} · {context.userStatus} ·{" "}
        {context.hasAccountNumber ? "Account created" : "No account number"}
      </span>
      {context.quickLinks.map((link) => (
        <a
          key={`${link.label}-${link.url}`}
          href={link.url}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: 12,
            color: "var(--blue-700)",
            textDecoration: "none",
            fontWeight: 600,
          }}
        >
          {link.label}
          <ExternalLink size={12} />
        </a>
      ))}
    </div>
  );
}

function TypingIndicator({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 14 }}>
      <div style={{ maxWidth: "70%" }}>
        <div style={{ fontSize: 11, color: "var(--neutral-500)", marginBottom: 4 }}>
          {label}
        </div>
        <div className="support-typing-bubble" aria-label={label}>
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  );
}

function MessageRow({ message }: { message: SupportMessage }) {
  const isUser = message.senderType === "user";
  const isSystem = message.senderType === "system";
  const [expandedImage, setExpandedImage] = useState<string | null>(null);

  if (isSystem) {
    return (
      <div style={{ textAlign: "center", fontSize: 12, color: "var(--neutral-500)", margin: "12px 0" }}>
        {message.body}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", justifyContent: isUser ? "flex-start" : "flex-end", marginBottom: 14 }}>
      <div style={{ maxWidth: "70%" }}>
        <div style={{ fontSize: 11, color: "var(--neutral-500)", marginBottom: 4 }}>
          {message.senderName || (isUser ? "User" : "Support")} · {formatDate(message.createdAt)}
        </div>
        <div
          style={{
            background: isUser ? "white" : "var(--blue-600)",
            color: isUser ? "var(--neutral-800)" : "white",
            border: "1px solid var(--neutral-300)",
            borderRadius: "var(--radius-md)",
            padding: "10px 12px",
            fontSize: 14,
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
          }}
        >
          {message.attachmentUrl ? (
            <button
              type="button"
              onClick={() => setExpandedImage(message.attachmentUrl ?? null)}
              style={{
                display: "block",
                padding: 0,
                border: "none",
                background: "transparent",
                cursor: "zoom-in",
                marginBottom: 8,
              }}
            >
              <img
                src={message.attachmentUrl}
                alt="Support attachment"
                style={{
                  display: "block",
                  maxWidth: 260,
                  maxHeight: 220,
                  objectFit: "cover",
                  borderRadius: "var(--radius-sm)",
                }}
              />
            </button>
          ) : null}
          {message.body}
        </div>
      </div>
      {expandedImage ? (
        <SupportImageLightbox imageUrl={expandedImage} onClose={() => setExpandedImage(null)} />
      ) : null}
    </div>
  );
}
